import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ReactPaginate from 'react-paginate'
import { Button, Card, CardHeader, Col, Progress, Row, Spinner, Table } from 'reactstrap'
import { api } from '../../utils'
import { getDownloadFilename, triggerBlobDownload } from '../../utils/download'
import { notify } from '../../utils/notify'
import SetTitle from '../../utils/set_title'
import VolumeRetrieveHistoricalRecovery from './VolumeRetrieveHistoricalRecovery'

const ITEM_PER_PAGE = 10
const LIVE_PROGRESS_STATUSES = new Set(['RETRIEVING_DATA'])
const UNVERIFIED_PROGRESS_STATUSES = new Set(['ERROR', 'INCOMPLETE'])
const EMPTY_PROGRESS_STATUSES = new Set([
  'PLANNED',
  'SUPERSEDED',
  'CREATED',
  'WAITING_FOR_DATA',
  'IMPORTING',
  'EXPORTING',
  'ERROR',
  'INCOMPLETE',
])

function sortExecutions(executions) {
  return [...executions].sort((left, right) => {
    const leftRunDate = left.run_date || left.date || ''
    const rightRunDate = right.run_date || right.date || ''

    if (leftRunDate !== rightRunDate) {
      return rightRunDate.localeCompare(leftRunDate)
    }

    return right.id - left.id
  })
}

function formatDateValue(value) {
  if (!value) {
    return '-'
  }

  return String(value).slice(0, 10)
}

function buildExecutionRows(executions, stats) {
  const orderedExecutions = sortExecutions(executions)

  return orderedExecutions.map((execution, index) => {
    const executionKeywordCount = Number(execution.keyword_count)
    const totalKeywords = Number.isFinite(executionKeywordCount)
      ? executionKeywordCount
      : stats.totalKeywords || 0
    const displayStatus = execution.status
    const shouldShowLiveProgress =
      index === 0 &&
      LIVE_PROGRESS_STATUSES.has(displayStatus) &&
      stats.totalKeywords === totalKeywords
    const filledKeywords = shouldShowLiveProgress
      ? stats.filledKeywords
      : EMPTY_PROGRESS_STATUSES.has(displayStatus) || LIVE_PROGRESS_STATUSES.has(displayStatus)
      ? 0
      : totalKeywords

    return {
      ...execution,
      display_status: displayStatus,
      next_retry_at: execution.next_retry_at,
      stats: {
        totalKeywords,
        filledKeywords,
        progressVerified:
          !UNVERIFIED_PROGRESS_STATUSES.has(displayStatus) &&
          (!LIVE_PROGRESS_STATUSES.has(displayStatus) || shouldShowLiveProgress) &&
          (displayStatus !== 'EXPORTED' || Boolean(execution.integrity_verified)),
      },
    }
  })
}

function VolumeRetrieveExecutions() {
  SetTitle('Market Screener Executions')

  const { id } = useParams()
  const history = useHistory()
  const [schedule, setSchedule] = useState(null)
  const [volumeReports, setVolumeReports] = useState([])
  const [pages, setPages] = useState(0)
  const [actualPage, setActualPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isRecoveryOpen, setIsRecoveryOpen] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const [keywordStats, setKeywordStats] = useState({
    totalKeywords: 0,
    filledKeywords: 0,
  })

  useEffect(() => {
    let isMounted = true

    async function load() {
      setLoading(true)
      setError(null)

      try {
        const [scheduleResponse, reportsResponse, keywordStatsResponse] = await Promise.all([
          api.get(`/market-screener-detail/${id}`),
          api.get('volume-report-runs/', {
            params: {
              schedule_id: id,
              page_size: ITEM_PER_PAGE,
              page: actualPage,
            },
          }),
          api.get('keyword-volume/', {
            params: { volume_report_scheduled_id: id },
          }),
        ])

        if (!isMounted) {
          return
        }

        const nextKeywordStats = {
          totalKeywords: keywordStatsResponse.data.total,
          filledKeywords: keywordStatsResponse.data.filled,
        }

        setKeywordStats(nextKeywordStats)
        setSchedule(scheduleResponse.data)
        setVolumeReports(buildExecutionRows(reportsResponse.data.results, nextKeywordStats))
        setPages(Math.ceil(reportsResponse.data.count / ITEM_PER_PAGE))
      } catch (loadError) {
        if (isMounted) {
          setError('Error fetching market screener executions.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      isMounted = false
    }
  }, [actualPage, id, reloadToken])

  const handlePageClick = (data) => {
    setActualPage(data.selected + 1)
  }

  const handleRecoverySuccess = () => {
    setActualPage(1)
    setReloadToken((value) => value + 1)
  }

  const handleDownloadKeywordsFile = async () => {
    try {
      const response = await api.get(`/market-screener-detail/${id}/download-keywords`, {
        responseType: 'blob',
      })
      const fileName = getDownloadFilename(
        response.headers?.['content-disposition'],
        'market_screener_keywords.xlsx'
      )
      triggerBlobDownload(response.data, fileName)
    } catch (downloadError) {
      notify('Impossibile scaricare le keyword.', 'error')
    }
  }

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <div className="text-center text-danger">{error}</div>
  }

  return (
    <>
      <VolumeRetrieveHistoricalRecovery
        isOpen={isRecoveryOpen}
        onClose={() => setIsRecoveryOpen(false)}
        onSuccess={handleRecoverySuccess}
        scheduleId={id}
        initialDetails={schedule}
      />
      <Row style={{ marginBottom: '100px' }}>
        <div className="col pb-5">
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row style={{ marginLeft: '0px' }}>
                <Col md={5}>
                  <h1 className="mb-0">Market Screener Executions</h1>
                </Col>
                <Col md={7}>
                  <div
                    data-testid="executions-header-actions"
                    className="d-flex justify-content-end align-items-center"
                    style={{
                      gap: '8px',
                      flexWrap: 'nowrap',
                      overflowX: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Button color="info" type="button" onClick={() => setIsRecoveryOpen(true)}>
                      Historical Recovery
                    </Button>
                    {keywordStats.totalKeywords > 0 && (
                      <Button color="secondary" type="button" onClick={handleDownloadKeywordsFile}>
                        Download keywords
                      </Button>
                    )}
                    <Button
                      color="warning"
                      type="button"
                      onClick={() => history.push('/market-screener')}
                    >
                      &lt;- Go Back
                    </Button>
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '80px' }}>
                    ID
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '220px' }}>
                    Name
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '120px' }}>
                    Status
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '150px' }}>
                    Trigger
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '130px' }}>
                    Run Date
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '130px' }}>
                    Next Retry
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '220px' }}>
                    Period Downloaded
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '110px' }}>
                    Keywords
                  </th>
                  <th scope="col" style={{ fontSize: '12px', minWidth: '160px' }}>
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody>
                {volumeReports.map((item) => {
                  const { totalKeywords, filledKeywords, progressVerified } = item.stats
                  const barClass = filledKeywords === totalKeywords ? 'bg-success' : 'bg-warning'

                  return (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.name}</td>
                      <td>{item.display_status}</td>
                      <td>{item.trigger || '-'}</td>
                      <td>{formatDateValue(item.run_date || item.date)}</td>
                      <td>
                        {item.display_status === 'EXPORTED'
                          ? '-'
                          : formatDateValue(item.next_retry_at)}
                      </td>
                      <td>{`${item.period_downloaded_start} : ${item.period_downloaded_end}`}</td>
                      <td>{totalKeywords}</td>
                      <td>
                        {progressVerified ? (
                          <div className="d-flex align-items-center">
                            <div style={{ width: '120px' }}>
                              <Progress
                                max={totalKeywords}
                                value={filledKeywords}
                                barClassName={barClass}
                              />
                            </div>
                            <span style={{ marginLeft: '5px', fontSize: '12px' }}>
                              {filledKeywords}/{totalKeywords}
                            </span>
                          </div>
                        ) : (
                          <span>Not verified</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            {pages > 0 ? (
              <ReactPaginate
                previousLabel={'<'}
                nextLabel={'>'}
                breakLabel={'...'}
                pageCount={pages}
                forcePage={actualPage - 1}
                onPageChange={handlePageClick}
                containerClassName={'pagination justify-content-center pt-5 pb-3'}
                pageClassName={'page-item'}
                pageLinkClassName={'page-link'}
                previousClassName={'page-item'}
                previousLinkClassName={'page-link'}
                nextClassName={'page-item'}
                nextLinkClassName={'page-link'}
                breakClassName={'page-item'}
                breakLinkClassName={'page-link'}
                activeClassName={'active'}
              />
            ) : null}
          </Card>
        </div>
      </Row>
    </>
  )
}

export default VolumeRetrieveExecutions
